import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  AppCard,
  AppModal,
  AppNoInfo,
  AppNotificationAlert,
} from "@/layout/AppComponents";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import errorHandler from "@/layout/components/error/ErrorHandler";
import { axiosPath, path } from "../../../routing/helpers/router";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import TotalEnergiesRiskSidebar from "../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Sidebar/TotalEnergiesRiskSidebar";
import AppLink from "../../../routing/helpers/AppLink";
import ControlFields from "../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Switch/Risk/ControlTab/ControlFields";
import * as moduleAction from "../../../store/actions/moduleAction";

export default function ModuleTotalEnergiesRiskControlHomeScreen() {
  const { moduleBridgeHex, riskID, controlHex, page, filtersHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const [loading, setLoading] = useState(false);
  const [control, setControl] = useState();
  const [showCancelModal, setShowCancelModal] = useState();
  const validationSchema = Yup.object().shape({
    riskID: Yup.number()
      .nullable()
      .moreThan(0, "Requerido")
      .required("Requerido"),
    name: Yup.string().required("Requerido"),
    responsibleID: Yup.number().nullable().required("Requerido"),
  });

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD CONTROL
  const loadControl = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_totalEnergiesRisk_control_get"), {
          moduleBridgeHex,
          controlHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            setControl(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, controlHex],
  );

  useEffect(() => {
    if (controlHex) {
      setLoading(true);
      loadControl();
    }
  }, []);
  // < LOAD CONTROL

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            controlHex
              ? "api_r_user_module_totalEnergiesRisk_control_edit"
              : "api_r_user_module_totalEnergiesRisk_control_add",
          ),
          {
            moduleBridgeHex,
            controlHex,
            ...values,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            AppNotificationAlert({
              title: "Completado",
              message: controlHex
                ? "Control editado con éxito"
                : "Control creado con éxito",
              type: "success",
              duration: 3000,
            });
            if (!controlHex) {
              navigate(
                path({
                  name: "user_module_totalEnergiesRisk_control_review_edit",
                  params: { moduleBridgeHex, reviewHex: data?.reviewHex },
                }),
              );
            }
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, controlHex],
  );
  // < SAVE

  // > CANCEL TOGGLE
  const handleCancelToggle = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_totalEnergiesRisk_control_cancel_toggle",
          ),
          {
            moduleBridgeHex,
            controlHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            AppNotificationAlert({
              title: "Completado",
              message: data?.active
                ? "El Control ha sido reactivado"
                : "El Control ha sido desactivado",
              type: "success",
              duration: 5000,
            });
            setControl({ ...control, active: data?.active });
            setShowCancelModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, control, controlHex],
  );
  // < CANCEL TOGGLE

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath("api_r_user_module_totalEnergiesRisk_getModuleData"),
        "moduleData",
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "permissions",
          {
            moduleBridgeHex,
            subType: "permissions",
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
          "userBridge",
          {
            moduleBridgeHex,
            subType: "userBridge",
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);

  if (!moduleData) return <AppNoInfo />;

  return (
    <>
      <TotalEnergiesRiskSidebar />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: "user_module_totalEnergiesRisk_control_list",
                    params: {
                      moduleBridgeHex,
                      page,
                      filtersHex,
                    },
                  },
                },
                {
                  name: "Controles",
                  route: {
                    name: "user_module_totalEnergiesRisk_control_list",
                    params: {
                      moduleBridgeHex,
                      page,
                      filtersHex,
                    },
                  },
                },
                { name: controlHex ? "Edición de Control" : "Nuevo Control" },
              ]
            : [{ name: "..." }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : (
            <Formik
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              initialValues={{
                name: control?.name ?? "",
                description: control?.description ?? "",
                riskID: control?.riskID ?? riskID ?? null,
                secondaryRiskIDs: control?.secondaryRiskIDs || [],
                controlType: control?.controlType ?? null,
                controlClass: control?.controlClass ?? null,
                frequency: control?.frequency ?? null,
                controlDocumentation: control?.controlDocumentation ?? null,
                controlEfficiency: control?.controlEfficiency ?? null,
                responsibleName: control?.responsibleName,
                responsibleID: control?.responsibleID,
                state: control?.state ?? 0,
              }}
              onSubmit={handleSave}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <AppCard
                    header={
                      <div>
                        {control ? "Edición de Control" : "Nuevo Control"}
                        {control && (
                          <button
                            type="button"
                            className={`btn btn-xs btn-${
                              control?.active ? "danger" : "success"
                            } float-right`}
                            onClick={() =>
                              control?.active
                                ? setShowCancelModal(true)
                                : handleCancelToggle()
                            }
                          >
                            {control?.active ? "Desactivar" : "Activar"}
                          </button>
                        )}
                      </div>
                    }
                  >
                    <ControlFields
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      hasState={!!controlHex}
                    />
                  </AppCard>
                  <div className="row mt-3">
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-sm btn-success float-right"
                        disabled={isSubmitting || (control && !control?.active)}
                      >
                        {controlHex ? "Editar" : "Crear"}
                      </button>
                      <AppLink
                        path="user_module_totalEnergiesRisk_control_list"
                        params={{
                          moduleBridgeHex,
                          page,
                          filtersHex,
                        }}
                        className="btn btn-sm btn-outline-danger float-right mr-2"
                      >
                        Volver
                      </AppLink>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          )}
        </div>
      </main>
      <AppModal
        show={showCancelModal}
        title="Desactivar Control"
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelToggle}
        isConfirmBtn
        isCancelBtn
      >
        <h6>Riesgo principal</h6>
        <div>{control?.riskCode}</div>
        {control?.secondaryRiskCodes?.length > 0 && (
          <>
            <h6 className="mt-3">Riesgos secundarios</h6>
            <ul>
              {control?.secondaryRiskCodes?.map((src) => (
                <li>{src}</li>
              ))}
            </ul>
          </>
        )}
      </AppModal>
    </>
  );
}
